import { isSupported as isFirebaseSupported } from "firebase/messaging";
import { useRef, useEffect } from "react";
import { useShouldShowDarkMode } from "../api/settings/useShouldShowDarkMode";
import { AppType } from "../constants/AppType";
import { getAppType } from "../getAppType";
import { useEvaluatedLocale } from "../localization/useEvaluatedLocale";
import { useSettings } from "../settings/useSettings";
import { useWhoAmI } from "../user/useWhoAmI";
import { fcmToken, fcmTokenInitialized } from "./firebaseStuff";

// check preconditions before loading any of the firebase code

export function usePushNotificationsConditionally() {
  const settings = useSettings();
  const { selectedVerifiedProfileInConcernForThisSession, whoAmI } = useWhoAmI();
  const { darkMode } = useShouldShowDarkMode();
  const evaluatedLocale = useEvaluatedLocale();

  let n = useRef(0);
  async function initFirebaseCode() {
    if (n.current++ > 0) return;

    // conditionally await import of the other firebase code to prevent errors in unsupported browsers
    const isSupported = await isFirebaseSupported();

    if (!isSupported) {
      console.error(
        `"a92209f08e1f" Firebase is not supported by browser. concernId=${whoAmI.concernId} idCandidate=${whoAmI.idCandidate}`,
        window.navigator.userAgent
      );
      return;
    }

    const module = await import("./initializePushNotifications");
    module.initializePushNotifications(whoAmI, darkMode, evaluatedLocale);
  }

  function shouldTryInit() {
    const enablePush = settings.pwa_enabled;

    if (!enablePush) return false;
    if (!selectedVerifiedProfileInConcernForThisSession) return false;
    if (getAppType() === AppType.iframe) return false;
    if (window.iswtapp) return false; // native app handles push notifications
    return true;
  }

  const tryInit = shouldTryInit();

  useEffect(() => {
    if (tryInit) {
      initFirebaseCode();
    }
  }, [tryInit]);

  return {
    fcmToken: fcmToken.get(),
    fcmTokenInitialized: fcmTokenInitialized.get(),
  };
}
